import React from "react";
import { Helmet } from "react-helmet-async";
import { EventNames, trackEvent } from "../analytics";
import { canonicalUrl } from "../siteConfig";

const pricingTiers = [
  {
    name: "Starter",
    price: "$297",
    scope: "For one priority URL or a very small site that needs a concise technical SEO and discoverability review.",
  },
  {
    name: "Growth",
    price: "$697",
    scope: "For a business with several priority pages, local or service-page visibility concerns, and measurement gaps.",
  },
  {
    name: "Advanced",
    price: "$1,500",
    scope: "For larger sites or deeper reviews across page structure, rendering risks, analytics, and implementation order.",
  },
];

const reviewedAreas = [
  "crawlability",
  "indexability",
  "metadata",
  "structured data",
  "Core Web Vitals",
  "rendering issues",
  "analytics setup",
  "conversion tracking",
  "AI discoverability",
];

const exclusions = [
  "backlink campaigns",
  "broad keyword strategy",
  "content marketing retainers",
  "paid ads management",
  "rankings, traffic, lead, or revenue guarantees",
];

export default function TechnicalVisibilityAuditPage() {
  const trackAssessmentCta = (source) => trackEvent(EventNames.AssessmentCtaClicked, { source });
  const trackScanCta = (ctaId) => trackEvent(EventNames.CtaClicked, { cta_id: ctaId });

  return (
    <div className="app page-grid">
      <Helmet>
        <title>Technical SEO & Visibility Assessment | OptiSiteSEO</title>
        <meta
          name="description"
          content="A paid diagnostic review of technical SEO, crawlability, indexing, structured data, Core Web Vitals, rendering issues, AI discoverability, analytics, and measurement gaps."
        />
        <link rel="canonical" href={canonicalUrl("/technical-visibility-audit")} />
        <meta property="og:title" content="Technical SEO & Visibility Assessment | OptiSiteSEO" />
        <meta
          property="og:description"
          content="Starter $297, Growth $697, Advanced $1,500. Manual technical SEO review with a prioritized implementation roadmap."
        />
        <meta property="og:url" content={canonicalUrl("/technical-visibility-audit")} />
      </Helmet>

      <nav className="top-nav">
        <div className="brand">
          <span>OptiSiteSEO</span>
        </div>
        <div className="nav-links">
          <a className="link" href="/">
            Home
          </a>
          <a className="link" href="/demo/index.html">
            Sample report
          </a>
          <a className="link" href="/what-we-check/index.html">
            What we check
          </a>
          <a className="primary" href="/app/index.html" onClick={() => trackScanCta("assessment-details-nav-scan")}>
            Run Free SEO Scan
          </a>
        </div>
      </nav>

      <header className="service-hero audit-hero">
        <div className="service-hero-copy">
          <p className="eyebrow">Paid diagnostic after the Free SEO Scan</p>
          <h1>Technical SEO & Visibility Assessment</h1>
          <p className="subtext">
            A manual diagnostic review for businesses that need to understand which technical SEO issues, discoverability blockers, crawlability and
            indexing problems, performance and measurement gaps, and AI discoverability readiness issues should be fixed first.
          </p>
          <div className="hero-actions">
            <a className="primary" href="/request-assessment" onClick={() => trackAssessmentCta("assessment_details_hero")}>
              Request Assessment
            </a>
            <a className="secondary" href="/app/index.html" onClick={() => trackScanCta("assessment-details-hero-scan")}>
              Run Free SEO Scan
            </a>
          </div>
          <div className="badge-row">
            <span className="badge">Starter $297</span>
            <span className="badge">Growth $697</span>
            <span className="badge">Advanced $1,500</span>
          </div>
        </div>
        <aside className="offer-summary" aria-label="Technical SEO & Visibility Assessment summary">
          <p className="eyebrow">What it is</p>
          <h2>A prioritized implementation roadmap</h2>
          <p>
            The assessment turns scan findings and priority-page review into a first-next-later roadmap for technical SEO implementation, measurement
            cleanup, and ongoing visibility monitoring.
          </p>
          <div className="offer-facts">
            <div>
              <span className="label">Paid diagnostic</span>
              <strong>$297-$1,500</strong>
            </div>
            <div>
              <span className="label">Next offer</span>
              <strong>Implementation package</strong>
            </div>
          </div>
          <a className="secondary" href="/request-assessment" onClick={() => trackAssessmentCta("assessment_details_summary")}>
            Request Assessment
          </a>
        </aside>
      </header>

      <section className="info-columns section" aria-label="Assessment overview">
        <div className="plain-panel">
          <p className="eyebrow">Who it is for</p>
          <h2>Businesses that need technical priorities</h2>
          <ul className="bullets">
            <li>Local, service, ecommerce, or SaaS sites with unclear technical visibility issues</li>
            <li>Owners and operators who know the site is underperforming but need to know what to fix first</li>
            <li>Teams that need scoped implementation priorities before assigning internal or external development work</li>
          </ul>
        </div>
        <div className="plain-panel">
          <p className="eyebrow">What is reviewed</p>
          <h2>Technical SEO, measurement, and AI readiness</h2>
          <ul className="bullets">
            {reviewedAreas.map((area) => (
              <li key={area}>{area}</li>
            ))}
          </ul>
        </div>
        <div className="plain-panel">
          <p className="eyebrow">Deliverables</p>
          <h2>Clear next steps for implementation</h2>
          <ul className="bullets">
            <li>Manual review of the Free SEO Scan result and priority public pages</li>
            <li>Prioritized findings across crawlability, indexability, structured data, performance, analytics, and AI discoverability</li>
            <li>First-next-later implementation roadmap with scope boundaries and recommended sequencing</li>
          </ul>
        </div>
      </section>

      <section className="paid-offer-band section" id="pricing">
        <div className="section-header">
          <div>
            <p className="eyebrow">Pricing tiers</p>
            <h2>Choose the assessment depth that matches the site</h2>
          </div>
        </div>
        <div className="info-columns">
          {pricingTiers.map((tier) => (
            <div className="plain-panel" key={tier.name}>
              <p className="eyebrow">{tier.name}</p>
              <h2>{tier.price}</h2>
              <p>{tier.scope}</p>
            </div>
          ))}
        </div>
        <div className="cta-row" style={{ marginTop: 18 }}>
          <a className="primary" href="/request-assessment" onClick={() => trackAssessmentCta("assessment_details_pricing")}>
            Request Assessment
          </a>
          <a className="secondary" href="/app/index.html" onClick={() => trackScanCta("assessment-details-pricing-scan")}>
            Run Free SEO Scan
          </a>
        </div>
      </section>

      <section className="card section compact-section" aria-label="What is not included">
        <div className="section-header">
          <div>
            <p className="eyebrow">Not included</p>
            <h2>Not a generic SEO agency package</h2>
          </div>
        </div>
        <ul className="bullets">
          {exclusions.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="paid-offer-band section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Offer ladder</p>
            <h2>Free SEO Scan, assessment, implementation, monitoring</h2>
          </div>
        </div>
        <ul className="bullets">
          <li>Free SEO Scan: public technical SEO checks for one URL</li>
          <li>Technical SEO & Visibility Assessment ($297-$1,500): paid diagnostic and prioritized roadmap</li>
          <li>Technical SEO Implementation Package ($1,500-$7,500): scoped implementation of selected fixes</li>
          <li>Monitoring & Visibility Platform ($750-$5,000/month): ongoing visibility monitoring, measurement review, and priority recommendations</li>
        </ul>
        <div className="cta-row" style={{ marginTop: 18 }}>
          <a className="primary" href="/request-assessment" onClick={() => trackAssessmentCta("assessment_details_bottom")}>
            Request Assessment
          </a>
        </div>
      </section>
    </div>
  );
}
